import preact from '../js/lib/preact';
import { Config, PS } from './client-main';
import { BattleNatures } from './battle-dex-data';
import { Dex, toID } from './battle-dex';
import type { RoomID } from './client-main';

type Match = {
	id: string; players: string[]; names: string[]; status: string; ready: string[];
	currentRoom: string | null; games: { room: string; winner: string }[];
};
type Profile = { id: string; name: string; sprite: string; team: string; sets: any[]; archive?: boolean; note?: string };
type TournamentState = {
	players: { id: string; name: string; busy: boolean; sprite: string }[];
	profiles: Profile[]; sprites: { id: string; name: string; url: string }[];
	viewer: { id: string; admin: boolean } | null;
	invites: { id: string; from: string; to: string }[];
	matches: Match[]; teamReady: boolean;
};
const emptyState: TournamentState = {
	players: [], profiles: [], sprites: [], viewer: null, invites: [], matches: [], teamReady: false,
};

export class EmerurdHome extends preact.Component<{ teamsPage?: boolean }> {
	override state = {
		nickname: localStorage.getItem('emerurd-nickname') || '', password: '',
		error: '', notice: '', pending: false, selected: '', draft: '', sprite: 'brendan',
		newName: '', newPassword: '', resetPassword: '', spriteSearch: '',
		tournament: emptyState,
		megaPreviews: {} as Record<string, boolean>,
		archive: [] as Profile[], archiveError: '', archiveLoading: false,
	};
	poll?: ReturnType<typeof setInterval>;
	archiveRequest?: AbortController;
	loadArchive = async () => {
		this.archiveRequest?.abort();
		const request = this.archiveRequest = new AbortController();
		this.setState({ archiveLoading: true, archiveError: '' });
		try {
			const response = await fetch('/data/heartandsoul-teams.json', { signal: request.signal });
			if (!response.ok) throw new Error('Archive unavailable');
			const archive: Profile[] = await response.json();
			if (!Array.isArray(archive) || archive.length !== 6) throw new Error('Invalid archive');
			if (!request.signal.aborted) this.setState({ archive, archiveLoading: false });
		} catch {
			if (!request.signal.aborted) this.setState({ archiveError: 'Could not load last tournament teams.', archiveLoading: false });
		}
	};
	command = (action: string, value = '') => {
		this.setState({ error: '', notice: '' });
		PS.send(`/emerurd ${action}${value ? ` ${value}` : ''}`);
	};
	refresh = () => {
		if (PS.connection?.connected) PS.send('/emerurd status');
	};
	response = (event: Event) => {
		const { type, data } = (event as CustomEvent).detail;
		if (type === 'emerurd') this.setState({ tournament: data });
		if (type === 'emerurderror') this.setState({ error: data, pending: false });
		if (type === 'emerurdsaved') {
			this.setState({ notice: data, pending: false, newPassword: '', resetPassword: '' });
		}
		if (type === 'emerurdauth') {
			sessionStorage.setItem('emerurd-session', data.token);
			this.setState({ password: '', pending: false });
			this.refresh();
		}
		if (type === 'emerurdlogout') {
			sessionStorage.removeItem('emerurd-session');
			location.assign('/');
		}
		if (type === 'emerurdbattle') PS.join(data as RoomID);
	};
	override componentDidMount() {
		window.addEventListener('emerurd', this.response);
		this.poll = setInterval(this.refresh, 3000);
		this.refresh();
		if (this.props.teamsPage) void this.loadArchive();
	}
	override componentWillUnmount() {
		window.removeEventListener('emerurd', this.response);
		clearInterval(this.poll);
		this.archiveRequest?.abort();
	}
	login = (event: Event) => {
		event.preventDefault();
		localStorage.setItem('emerurd-nickname', this.state.nickname.trim());
		this.setState({ pending: true });
		this.command('login', JSON.stringify({ name: this.state.nickname.trim(), password: this.state.password }));
	};
	selectPlayer = (id: string) => {
		const player = this.state.tournament.profiles.find(p => p.id === id);
		this.setState({ selected: id, draft: player?.team || '', sprite: player?.sprite || 'brendan', resetPassword: '', notice: '' });
	};
	natureLabel(name = 'Serious') {
		const nature = BattleNatures[name as keyof typeof BattleNatures];
		const stats: Record<string, string> = {
			atk: 'Attack', def: 'Defense', spa: 'Sp. Atk', spd: 'Sp. Def', spe: 'Speed',
		};
		return nature?.plus && nature?.minus ?
			`${name} (+${stats[nature.plus]}, −${stats[nature.minus]})` : `${name} (neutral)`;
	}
	renderTeam(profile: Profile) {
		return this.renderPublicTeam(profile, true);
	}
	statValue(set: any, stat: string) {
		if (set.archiveStats) return set.archiveStats[stat];
		const base = (Dex.species.get(set.species).baseStats as any)[stat];
		const level = set.level || 50;
		const iv = set.ivs?.[stat] ?? 31;
		if (stat === 'hp') return base === 1 ? 1 : Math.floor((2 * base + iv) * level / 100) + level + 10;
		const nature = (BattleNatures as any)[set.nature || 'Serious'];
		const value = Math.floor((2 * base + iv) * level / 100) + 5;
		return Math.floor(value * (nature?.plus === stat ? 110 : nature?.minus === stat ? 90 : 100) / 100);
	}
	movePP(moveName: string, upgrades: number) {
		const move = Dex.moves.get(moveName);
		return move.noPPBoosts ? move.pp : move.pp * (5 + upgrades) / 5;
	}
	renderPublicTeam(profile: Profile, expanded = false) {
		const TeamDetails = expanded ? 'div' : 'details';
		const sprite = (set: any) => Config.emerurdGraphics ? `emerurd/front${set.shiny ? '-shiny' : ''}/${toID(set.species)}.png` : Dex.getSpriteData(set.species, true, { gen: 9, shiny: set.shiny, noScale: true }).url;
		return <article class="emerurd-roster-card" key={profile.id}>
			<header><img src={`emerurd/trainers/${profile.sprite}.png`} alt="" /><div><span>TRAINER</span><h3>{profile.name}</h3></div></header>
			{profile.note && <p class="emerurd-roster-empty">{profile.note}</p>}
			{!profile.team ? <p class="emerurd-roster-empty">No team assigned.</p> : <TeamDetails class="emerurd-roster-details">
				{!expanded && <summary><div class="emerurd-roster-six">{profile.sets.map((set, i) => <div key={i}><img src={sprite(set)} alt="" /><span>{set.name && set.name !== set.species && <strong>{set.name}<br /></strong>}{set.species}</span></div>)}</div><span class="emerurd-roster-toggle"><span class="when-closed">View team</span><span class="when-open">Hide team</span><span aria-hidden="true"> ↕</span></span></summary>}
				<div class="emerurd-roster-panels">{profile.sets.map((original, i) => {
					const species = Dex.species.get(original.species);
					const megaName = !profile.archive && Dex.items.get(original.item || '').megaStone?.[species.name];
					const mega = megaName ? Dex.species.get(megaName) : null;
					const key = `${profile.id}:${i}:${original.species}:${original.item || ''}`;
					const showingMega = !!mega?.exists && !!this.state.megaPreviews[key];
					const set = showingMega ? { ...original, species: mega!.name, ability: mega!.abilities['0'] } : original;
					return <section class="emerurd-roster-mon" key={i}>
					<div class="emerurd-roster-mon-heading"><div class="emerurd-roster-portrait"><img src={sprite(set)} alt="" />{set.item && <span class="emerurd-roster-item" role="img" aria-label={set.item} title={set.item} style={Dex.getItemIcon(set.item)} />}</div><div><h4>{set.name || set.species}</h4><p>{set.species} · Lv. {set.level || 50}{set.gender === 'M' || set.gender === 'F' ? <span aria-label={set.gender === 'M' ? 'Male' : 'Female'}> · {set.gender === 'M' ? '♂' : '♀'}</span> : null}{set.shiny ? ' · Shiny' : ''}</p></div></div>
					{mega?.exists && <button type="button" class="button" aria-pressed={showingMega} onClick={() => this.setState({
						megaPreviews: { ...this.state.megaPreviews, [key]: !showingMega },
					})}>{showingMega ? 'Show base stats' : 'Show Mega stats'}</button>}
					<dl class="emerurd-roster-fields">{[['Ability', set.ability], ['Nature', this.natureLabel(set.nature || 'Serious')]].map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}</dl>
					<h5>Moves</h5><ul class="emerurd-roster-moves">{set.moves.map((move: string, j: number) => {
						const data = Dex.moves.get(move);
						let type = data.exists ? data.type : '';
						if (data.id === 'hiddenpower') {
							const types = ['Fighting', 'Flying', 'Poison', 'Ground', 'Rock', 'Bug', 'Ghost', 'Steel', 'Fire', 'Water', 'Grass', 'Electric', 'Psychic', 'Ice', 'Dragon', 'Dark'];
							const bits = ['hp', 'atk', 'def', 'spe', 'spa', 'spd'].reduce((sum, stat, i) => sum + ((set.ivs?.[stat] ?? 31) % 2) * (1 << i), 0);
							type = types[Math.floor(bits * 15 / 63)];
						}
						return <li key={j}><span class="emerurd-move-heading"><span>{move}</span>{type && <img src={`/emerurd/types/${type}.png`} alt={`${type} type`} title={`${type} type`} />}</span>{!profile.archive && <small>PP {this.movePP(move, set.ppUps?.[j] || 0)}/{this.movePP(move, set.ppUps?.[j] || 0)}</small>}</li>;
					})}</ul>
					<div class="emerurd-roster-stats">{[['hp', 'HP'], ['atk', 'Atk'], ['def', 'Def'], ['spa', 'Sp. Atk'], ['spd', 'Sp. Def'], ['spe', 'Speed']].map(([stat, label]) => <div class={`emerurd-stat-row stat-band-${this.statValue(set, stat) < 75 ? 1 : this.statValue(set, stat) < 100 ? 2 : this.statValue(set, stat) < 125 ? 3 : this.statValue(set, stat) < 150 ? 4 : this.statValue(set, stat) < 175 ? 5 : 6}`} key={stat}>
						<span>{label}</span><span class="emerurd-stat-track" aria-hidden="true"><span style={`width:${Math.min(100, this.statValue(set, stat) / 3)}%`} /></span><strong>{this.statValue(set, stat)}</strong><small>IV {set.ivs?.[stat] ?? 31}</small>
					</div>)}</div>
					<p class="emerurd-roster-meta">{profile.archive ? `${set.statSource} · ${set.ivSource} · ${set.evSource}` : `EVs all 0 · Friendship ${set.happiness ?? 255}`}</p>
				</section>;
				})}</div>
				<details class="emerurd-roster-export"><summary>Showdown team text</summary><pre class="emerurd-export">{profile.team}</pre></details>
			</TeamDetails>}
		</article>;
	}
	renderAdmin() {
		const { tournament } = this.state;
		return <section class="emerurd-card emerurd-admin"><h2>Admin · players and teams</h2>
			<form onSubmit={e => {
				e.preventDefault(); this.setState({ pending: true });
				this.command('create', JSON.stringify({ name: this.state.newName, password: this.state.newPassword }));
			}}><h3>Create player</h3>
				<label>New player name<input maxLength={18} value={this.state.newName} onInput={e => this.setState({ newName: (e.currentTarget as HTMLInputElement).value })} required /></label>
				<label>Initial password<input type="password" minLength={6} maxLength={128} autocomplete="new-password" value={this.state.newPassword} onInput={e => this.setState({ newPassword: (e.currentTarget as HTMLInputElement).value })} required /></label>
				<button class="button" disabled={this.state.pending}>Create player</button>
			</form>
			<label>Manage player<select value={this.state.selected} onChange={e => this.selectPlayer((e.currentTarget as HTMLInputElement).value)}>
				<option value="">Choose a player</option>{tournament.profiles.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
			</select></label>
			{this.state.selected && <>
				<form onSubmit={e => {
					e.preventDefault(); this.setState({ pending: true });
					this.command('save', JSON.stringify({ id: this.state.selected, team: this.state.draft, sprite: this.state.sprite }));
				}}>
					<label>Showdown team text<textarea rows={14} spellcheck={false} maxLength={24000} value={this.state.draft} onInput={e => this.setState({ draft: (e.currentTarget as HTMLInputElement).value })} required /></label>
					<p>Six Pokémon · level 50 · all EVs forced to zero. PP Ups: 0–3 per move.</p>
					<label>Find trainer sprite<input type="search" value={this.state.spriteSearch} onInput={e => this.setState({ spriteSearch: (e.currentTarget as HTMLInputElement).value })} /></label>
					<div class="emerurd-sprite-picker" role="group" aria-label="Trainer sprites">{tournament.sprites.filter(s => s.name.toLowerCase().includes(this.state.spriteSearch.toLowerCase())).map(s =>
						<button type="button" key={s.id} class={this.state.sprite === s.id ? 'selected' : ''} aria-pressed={this.state.sprite === s.id} onClick={() => this.setState({ sprite: s.id })}>
							<img src={s.url} alt="" /><span>{s.name}</span></button>
					)}</div>
					<button class="button" disabled={this.state.pending}>{this.state.pending ? 'Saving…' : 'Validate and save team'}</button>
				</form>
				<h3>Saved team preview</h3>
				{tournament.profiles.filter(p => p.id === this.state.selected).map(p => this.renderTeam(p))}
				<form onSubmit={e => {
					e.preventDefault(); this.setState({ pending: true });
					this.command('password', JSON.stringify({ id: this.state.selected, password: this.state.resetPassword }));
				}}><label>New password<input type="password" minLength={6} maxLength={128} autocomplete="new-password" value={this.state.resetPassword} onInput={e => this.setState({ resetPassword: (e.currentTarget as HTMLInputElement).value })} required /></label>
					<button class="button" disabled={this.state.pending}>Reset password</button>
				</form>
			</>}
		</section>;
	}
	override render() {
		const { tournament, error } = this.state;
		const own = tournament.matches.filter(m => m.players.includes(PS.user.userid) && m.status !== 'complete');
		const busy = own.length > 0;
		const online = !!PS.connection?.connected;
		const profile = tournament.profiles.find(p => p.id === tournament.viewer?.id);
		return <main class="emerurd-home">
			<header class="emerurd-title">
				<h1>EMERURD</h1><p>by Sigurd and Simon</p>
				<span class={`emerurd-connection ${online ? 'online' : ''}`}>{online ? 'Connected' : 'Connecting…'}</span>
			</header>
			<nav class="emerurd-nav"><a href="/">Tournament</a><a href="/teams">All teams</a>
				{tournament.viewer && <><span>Signed in as {profile?.name}</span><button class="button" onClick={() => this.command('logout')}>Sign out</button></>}
			</nav>
			{error && <div class="emerurd-error" role="alert">{error}</div>}
			{this.state.notice && <p class="emerurd-notice" role="status">{this.state.notice}</p>}
			{this.props.teamsPage ? <><section><h2>Current tournament teams</h2><p>Full tournament teams · public team sheets</p>
				<div class="emerurd-roster-grid">{tournament.profiles.filter(p => !p.id.startsWith('testurd')).map(p => this.renderPublicTeam(p))}</div>
			</section><section class="emerurd-archive"><img class="emerurd-archive-logo" src="/emerurd/heartandsoul.png" alt="Pokémon Heart &amp; Soul title screen" /><h2>Last tournament — Heart &amp; Soul · Spring 2026</h2>
				<p>Historical teams · level 50 · zero EVs · original Heart &amp; Soul base stats</p>
				{this.state.archiveLoading && <p role="status">Loading last tournament teams…</p>}
				{this.state.archiveError && <p role="alert">{this.state.archiveError} <button type="button" class="button" onClick={this.loadArchive}>Retry</button></p>}
				<div class="emerurd-roster-grid">{this.state.archive.map(p => {
					const account = p.id === 'heartandsoul-pokuro' ? 'sigurd' : p.id === 'heartandsoul-simon' ? 'simon' : '';
					const current = account && tournament.profiles.find(profile => profile.id === account);
					return this.renderPublicTeam(current ? { ...p, sprite: current.sprite } : p);
				})}</div>
			</section></> : <>
				{!tournament.viewer ? <section class="emerurd-card"><h2>Enter the club</h2>
					<form onSubmit={this.login}>
						<label>Trainer username<input value={this.state.nickname} maxLength={18} onInput={e => this.setState({ nickname: (e.currentTarget as HTMLInputElement).value })} autocomplete="username" required /></label>
						<label>Password<input type="password" value={this.state.password} maxLength={128} onInput={e => this.setState({ password: (e.currentTarget as HTMLInputElement).value })} autocomplete="current-password" required /></label>
						<button class="button" disabled={!online || this.state.pending}>Sign in</button>
					</form><p>Ask Sigurd or Simon for a player account and team.</p>
				</section> : <div class="emerurd-columns">
					<div>{profile && this.renderTeam(profile)}</div>
					<section class="emerurd-card"><h2>Tournament floor</h2>
						<p>Doubles · choose 4 of 6 · level 50 · zero EVs · Mega Evolution · public sheets</p>
						{!tournament.teamReady && <p>Ask an admin to assign your team before challenging another player.</p>}
						{tournament.invites.map(invite => <div class="emerurd-invite" key={invite.id}>
							<p>{invite.to === PS.user.userid ? `${invite.from} challenges you to three games.` : `Waiting for ${invite.to}…`}</p>
							{invite.to === PS.user.userid && <button class="button" disabled={!tournament.teamReady} onClick={() => this.command('accept', invite.id)}>Accept match</button>} {' '}
							<button class="button" onClick={() => this.command('decline', invite.id)}>{invite.to === PS.user.userid ? 'Decline' : 'Cancel'}</button>
						</div>)}
						{own.map(match => this.renderMatch(match))}
						<h3>Online trainers</h3><ul class="emerurd-players">{tournament.players.filter(p => p.id !== PS.user.userid).map(player => <li key={player.id}>
							<img class="emerurd-trainer" src={`emerurd/trainers/${player.sprite}.png`} alt="" />
							<span>{player.name}<small>{player.busy ? 'In a match' : 'Available'}</small></span>
							<button class="button" disabled={player.busy || busy || !tournament.teamReady || tournament.invites.length > 0} onClick={() => this.command('challenge', player.id)}>Challenge</button>
						</li>)}</ul>
						{tournament.players.length <= 1 && <p>Waiting for another trainer to connect.</p>}
					</section>
				</div>}
				{tournament.viewer?.admin && this.renderAdmin()}
				<section class="emerurd-card emerurd-results"><h2>Matches & spectator links</h2>
					{!tournament.matches.length && <p>No matches yet.</p>}
					{[...tournament.matches].reverse().map(match => this.renderMatch(match))}
				</section>
			</>}
			<footer>Built on Pokémon Showdown · graphics and battle data from Emerurd · <a href="/source/">Source & licenses</a></footer>
		</main>;
	}
	renderMatch(match: Match) {
		const own = match.players.includes(PS.user.userid);
		const wins = match.players.map(p => match.games.filter(g => g.winner === p).length);
		return <article class="emerurd-match" key={match.id}>
			<h3>{match.names[0]} <span>{wins[0]} – {wins[1]}</span> {match.names[1]}</h3>
			<p>{match.games.length}/3 games complete · {match.status}
				{match.games.some(g => !g.winner) && ` · ${match.games.filter(g => !g.winner).length} drawn`}</p>
			<div class="emerurd-game-links">{match.games.map((game, i) => <a key={game.room} href={`/${game.room}`}>
				Game {i + 1}: {game.winner ? match.names[match.players.indexOf(game.winner)] : 'Draw'}</a>)}</div>
			{match.currentRoom && <a class="button" href={`/${match.currentRoom}`}>{own ? 'Return to battle' : 'Watch live'}</a>}
			{own && match.status === 'waiting' && <button class="button" disabled={match.ready.includes(PS.user.userid)}
				onClick={() => this.command('ready', match.id)}>{match.ready.includes(PS.user.userid) ? 'Waiting for opponent…' : `Ready for game ${match.games.length + 1}`}</button>}
			{own && match.status === 'interrupted' && <p>Ask the organizer to recover this match. Reference: {match.id}</p>}
		</article>;
	}
}
